import { useCallback, useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import {
  Anchor,
  Clock,
  ExternalLink,
  FileText,
  Hash,
  Loader2,
  Network,
  ShieldCheck,
  Users,
  Video,
  Download,
  Gavel,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { AnchorTriggerDialog } from "@/components/AnchorTriggerDialog";
import { FormationGate } from "@/components/FormationGate";
import { ArbiterCaseAcceptanceDialog } from "@/components/ArbiterCaseAcceptanceDialog";
import { ArbiterShell } from "@/components/ArbiterShell";
import { AssistantIAPanel } from "@/components/AssistantIAPanel";
import { SkillRunner } from "@/components/SkillRunner";
import { Button } from "@/components/ui/button";
import { CaseTimeline } from "@/components/CaseTimeline";
import { DocumentUpload } from "@/components/DocumentUpload";
import { ProcedureTimeline } from "@/components/ProcedureTimeline";
import { PhaseFrise } from "@/components/procedure/PhaseFrise";
import { ClotureDebatsButton } from "@/components/procedure/ClotureDebatsButton";
import { RedfernSchedule } from "@/components/procedure/RedfernSchedule";
import { MesuresProvisoires } from "@/components/procedure/MesuresProvisoires";
import { IncidentsPanel } from "@/components/procedure/IncidentsPanel";
import { DessaisiBadge } from "@/components/procedure/DessaisiBadge";
import type { PhaseProcedurale } from "@/types/procedure";
import { ProofBadge } from "@/components/ProofBadge";
import { ValidateStepDialog } from "@/components/ValidateStepDialog";
import { useAuth, useIsArbiterOrAdmin, useRole } from "@/contexts/AuthContext";
import {
  type AnchorStatusResponse,
  type AnchorTriggerResponse,
  type CaseDetail,
  downloadDocument,
  getAnchorStatus,
  getApiErrorDetail,
  getCaseDetail,
  verifyDocument,
} from "@/lib/api";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; detail: string }
  | { kind: "ready"; detail: CaseDetail };

const SECTIONS = [
  { id: "synthese", label: "Synthèse" },
  { id: "pieces", label: "Pièces" },
  { id: "ancrage", label: "Ancrage" },
  { id: "skills", label: "Skills IA" },
  { id: "membres", label: "Membres" },
  { id: "historique", label: "Historique" },
] as const;

export default function CaseDetailPage() {
  const [match, params] = useRoute<{ caseId: string }>("/cases/:caseId");
  const { session } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [anchor, setAnchor] = useState<AnchorStatusResponse | null>(null);
  const [anchorPolling, setAnchorPolling] = useState(false);
  const isArbiterOrAdmin = useIsArbiterOrAdmin();
  const role = useRole();

  const caseIdRaw = params?.caseId;
  const caseId = caseIdRaw ? Number.parseInt(caseIdRaw, 10) : Number.NaN;

  const reload = useCallback(async () => {
    if (!Number.isFinite(caseId)) {
      setState({ kind: "error", detail: "Identifiant invalide." });
      return;
    }
    try {
      const detail = await getCaseDetail(caseId);
      setState({ kind: "ready", detail });
    } catch (err) {
      setState({
        kind: "error",
        detail: getApiErrorDetail(err) ?? "Echec de chargement du dossier.",
      });
    }
  }, [caseId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const checkAnchor = async () => {
    if (!Number.isFinite(caseId) || anchorPolling) return;
    setAnchorPolling(true);
    try {
      const result = await getAnchorStatus(caseId);
      setAnchor(result);
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Echec vérification ancrage.");
    } finally {
      setAnchorPolling(false);
    }
  };

  const onVerifyDocument = async (ebid: string) => {
    try {
      const report = await verifyDocument(ebid);
      const ok = report.status === "COMPLIANT";
      (ok ? toast.success : toast.error)(
        `Vérification : ${report.status}` +
          (report.error_details ? ` -- ${report.error_details}` : ""),
      );
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Vérification impossible.");
    }
  };

  const onDownloadDocument = async (ebid: string, filename: string) => {
    try {
      await downloadDocument(ebid, filename);
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Téléchargement impossible.");
    }
  };

  if (!match) return null;

  const myMember =
    state.kind === "ready" && session
      ? state.detail.members.find((m) => m.user_id === session.user.id)
      : null;
  const isCaseArbiter = myMember?.member_role === "ARBITRE";
  const isCaseMember = !!myMember;

  const title =
    state.kind === "ready"
      ? `Dossier ${state.detail.numero_gsf}`
      : `Dossier #${caseIdRaw}`;

  const rightPanel = Number.isFinite(caseId) ? (
    <AssistantIAPanel
      caseFileId={caseId}
      scopeHint="Espace étanche dossier — secret du délibéré préservé. Aucune notification vers les parties."
    />
  ) : undefined;

  return (
    <ArbiterShell title={title} rightPanel={rightPanel}>
      {state.kind === "ready" && session && (
        <ArbiterCaseAcceptanceDialog
          caseId={caseId}
          userId={session.user.id}
          isCaseArbiter={Boolean(isCaseArbiter)}
        />
      )}

      {state.kind === "loading" && (
        <div className="flex items-center justify-center py-16">
          <Loader2
            className="animate-spin"
            size={28}
            style={{ color: "var(--proof-gold)" }}
          />
        </div>
      )}

      {state.kind === "error" && (
        <div className="px-4 sm:px-6 py-8 max-w-3xl mx-auto w-full">
          <div
            className="rounded-md border p-6 text-sm"
            style={{
              borderColor:
                "color-mix(in oklch, var(--block-red) 40%, transparent)",
              background:
                "color-mix(in oklch, var(--block-red) 10%, transparent)",
              color: "rgb(254 202 202)",
            }}
          >
            {state.detail}
            <div className="mt-3">
              <Button onClick={() => void reload()} size="sm">
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      )}

      {state.kind === "ready" && (
        <div className="px-4 sm:px-6 pt-4 max-w-5xl mx-auto w-full space-y-3">
          <PhaseFrise
            currentPhase={
              state.detail.phase_procedurale as PhaseProcedurale
            }
          />
          <DessaisiBadge
            phase={state.detail.phase_procedurale as PhaseProcedurale}
          />
          {isArbiterOrAdmin && (
            <ClotureDebatsButton
              caseId={caseId}
              phase={state.detail.phase_procedurale as PhaseProcedurale}
              onRefresh={() => void reload()}
            />
          )}
          {isArbiterOrAdmin && <RedfernSchedule caseId={caseId} isArbiter />}
          {(isArbiterOrAdmin || role === "GREFFIER" || isCaseMember) && (
            <>
              <IncidentsPanel
                caseId={caseId}
                canRaise={isCaseMember}
                role={role}
              />
              <MesuresProvisoires
                caseId={caseId}
                canRequest={isCaseMember}
                isTribunal={isArbiterOrAdmin}
              />
            </>
          )}
        </div>
      )}
      {state.kind === "ready" && (
        <CaseWorkspace
          detail={state.detail}
          isArbiterOrAdmin={isArbiterOrAdmin}
          anchor={anchor}
          anchorPolling={anchorPolling}
          onCheckAnchor={checkAnchor}
          onAnchorTriggered={(res) => {
            setAnchor(buildAnchorFromTrigger(res));
            void reload();
          }}
          onValidated={() => void reload()}
          onVerifyDocument={onVerifyDocument}
          onDownloadDocument={onDownloadDocument}
          onUploaded={() => void reload()}
          caseId={caseId}
        />
      )}
    </ArbiterShell>
  );
}

function buildAnchorFromTrigger(
  res: AnchorTriggerResponse,
): AnchorStatusResponse {
  return {
    dossier_id: res.dossier_id,
    anchored_status: res.anchored_status,
    tx_hash: res.tx_hash,
    anchored_merkle_root: res.anchored_merkle_root,
    anchored_at: new Date().toISOString(),
    block: null,
    explorer_url: res.explorer_url,
  };
}

// =============================================================================
// Workspace unifie verticalement
// =============================================================================

interface CaseWorkspaceProps {
  detail: CaseDetail;
  caseId: number;
  isArbiterOrAdmin: boolean;
  anchor: AnchorStatusResponse | null;
  anchorPolling: boolean;
  onCheckAnchor: () => void;
  onAnchorTriggered: (res: AnchorTriggerResponse) => void;
  onValidated: () => void;
  onVerifyDocument: (ebid: string) => void;
  onDownloadDocument: (ebid: string, filename: string) => void;
  onUploaded: () => void;
}

function CaseWorkspace({
  detail,
  caseId,
  isArbiterOrAdmin,
  anchor,
  anchorPolling,
  onCheckAnchor,
  onAnchorTriggered,
  onValidated,
  onVerifyDocument,
  onDownloadDocument,
  onUploaded,
}: CaseWorkspaceProps) {
  return (
    <div className="flex-1 flex flex-col">
      <div
        className="px-4 sm:px-6 py-3 border-b border-border"
        style={{ background: "var(--graphite-800)" }}
      >
        <ProcedureTimeline
          currentState={detail.statut}
          deadlineISO={detail.deadline_instruction}
        />
      </div>

      <nav
        className="sticky top-12 z-20 border-b border-border px-4 sm:px-6 backdrop-blur-sm"
        style={{
          background:
            "color-mix(in oklch, var(--graphite-900) 92%, transparent)",
        }}
        aria-label="Sections du dossier"
      >
        <ul className="flex items-center gap-4 overflow-x-auto h-10">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className="text-caption-arbitre block py-1 transition-colors hover:text-foreground"
                style={{ color: "var(--arbitre-slate-300)" }}
              >
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="px-4 sm:px-6 py-6 max-w-5xl mx-auto w-full space-y-8">
        <SyntheseSection
          detail={detail}
          isArbiterOrAdmin={isArbiterOrAdmin}
          onValidated={onValidated}
        />

        <PiecesSection
          detail={detail}
          caseId={caseId}
          onUploaded={onUploaded}
          onVerify={onVerifyDocument}
          onDownload={onDownloadDocument}
        />

        <AnchorSection
          detail={detail}
          anchor={anchor}
          polling={anchorPolling}
          onCheck={onCheckAnchor}
          isArbiterOrAdmin={isArbiterOrAdmin}
          onTriggered={onAnchorTriggered}
        />

        <SkillsSection
          caseId={caseId}
          isArbiterOrAdmin={isArbiterOrAdmin}
        />

        <MembresSection members={detail.members} />

        <HistoriqueSection detail={detail} />
      </div>
    </div>
  );
}

// =============================================================================
// Sections
// =============================================================================

function SectionTitle({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <h2
      className="flex items-center gap-2 text-caption-arbitre"
      style={{ color: "var(--proof-gold)" }}
    >
      {icon}
      {children}
    </h2>
  );
}

function SyntheseSection({
  detail,
  isArbiterOrAdmin,
  onValidated,
}: {
  detail: CaseDetail;
  isArbiterOrAdmin: boolean;
  onValidated: () => void;
}) {
  return (
    <section id="synthese" className="gsf-surface p-5 sm:p-6 scroll-mt-24">
      <div
        className="text-caption-arbitre flex items-center gap-1.5 mb-2"
        style={{ color: "var(--proof-gold)" }}
      >
        <Hash size={11} />
        <span className="gsf-mono-proof">{detail.numero_gsf}</span>
      </div>
      <h1 className="text-h1-arbitre">{detail.objet_litige}</h1>
      <div
        className="mt-3 flex flex-wrap gap-3 text-xs"
        style={{ color: "var(--arbitre-slate-300)" }}
      >
        <span>
          <FileText size={11} className="inline mr-1" />
          Statut : <strong>{detail.statut}</strong>
        </span>
        {detail.deadline_instruction && (
          <span>⏰ Deadline : {fmtDate(detail.deadline_instruction)}</span>
        )}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        <Link href={`/audiences/${detail.id}`}>
          <Button
            size="sm"
            style={{
              background: "var(--proof-gold)",
              color: "var(--graphite-900)",
            }}
          >
            <Video size={14} className="mr-2" />
            Ouvrir l'audience visio
          </Button>
        </Link>
        {isArbiterOrAdmin && (
          <>
            <ValidateStepDialog
              caseId={detail.id}
              currentState={detail.statut}
              onValidated={onValidated}
            />
            <Link href={`/cases/${detail.id}/decisions`}>
              <Button
                size="sm"
                variant="outline"
                style={{ color: "var(--proof-gold)" }}
              >
                <Gavel size={14} className="mr-2" />
                Décisions arbitrales
              </Button>
            </Link>
          </>
        )}
      </div>
    </section>
  );
}

function PiecesSection({
  detail,
  caseId,
  onUploaded,
  onVerify,
  onDownload,
}: {
  detail: CaseDetail;
  caseId: number;
  onUploaded: () => void;
  onVerify: (ebid: string) => void;
  onDownload: (ebid: string, filename: string) => void;
}) {
  return (
    <section id="pieces" className="scroll-mt-24">
      <SectionTitle icon={<FileText size={12} />}>
        Pièces probatoires ({detail.documents.length})
      </SectionTitle>
      <div className="mt-3 space-y-3">
        <DocumentUpload caseId={caseId} onUploaded={onUploaded} />
        {detail.documents.length === 0 && (
          <p
            className="text-sm italic"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            Aucune pièce versée pour l'instant.
          </p>
        )}
        {detail.documents.map((d) => (
          <DocumentRow
            key={d.id}
            doc={d}
            onVerify={onVerify}
            onDownload={onDownload}
          />
        ))}
      </div>
    </section>
  );
}

function AnchorSection({
  detail,
  anchor,
  polling,
  onCheck,
  isArbiterOrAdmin,
  onTriggered,
}: {
  detail: CaseDetail;
  anchor: AnchorStatusResponse | null;
  polling: boolean;
  onCheck: () => void;
  isArbiterOrAdmin: boolean;
  onTriggered: (res: AnchorTriggerResponse) => void;
}) {
  const status = anchor?.anchored_status ?? detail.anchored_status;
  const txHash = anchor?.tx_hash ?? detail.blockchain_tx_hash;
  const explorerUrl = anchor?.explorer_url ?? detail.explorer_url;
  const block = anchor?.block;

  const stepClass =
    status === "SUCCESS"
      ? "step-termine"
      : status === "PENDING"
        ? "step-instruction"
        : status === "FAILED"
          ? "step-failed"
          : "";

  const noDocs = detail.documents.length === 0;
  const alreadyPending = status === "PENDING";

  return (
    <section id="ancrage" className="scroll-mt-24">
      <SectionTitle icon={<Anchor size={12} />}>Ancrage Polygon</SectionTitle>
      <FormationGate parcours="ARBITRE" feature="anchoring">
      <div className="mt-3 gsf-surface p-5 flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {status === "SUCCESS" && (
                <ProofBadge
                  variant="filled"
                  icon={<Anchor size={11} />}
                  title="Racine Merkle ancrée on-chain"
                >
                  Polygon
                </ProofBadge>
              )}
              <span
                className={`text-caption-arbitre ${stepClass}`}
                style={{ letterSpacing: "0.12em" }}
              >
                {status}
                {block !== null && block !== undefined
                  ? ` · block #${block}`
                  : ""}
              </span>
            </div>
            {txHash ? (
              <div
                className="mt-1.5 gsf-mono-proof text-[11px] break-all"
                style={{ color: "var(--arbitre-slate-300)" }}
              >
                {txHash}
              </div>
            ) : (
              <div
                className="mt-1.5 text-xs italic"
                style={{ color: "var(--arbitre-slate-500)" }}
              >
                Aucun ancrage on-chain pour ce dossier.
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {explorerUrl && (
              <a href={explorerUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  size="sm"
                  variant="ghost"
                  style={{ color: "var(--proof-gold)" }}
                >
                  <ExternalLink size={14} className="mr-2" />
                  Polygonscan
                </Button>
              </a>
            )}
            <Button
              size="sm"
              onClick={onCheck}
              disabled={polling}
              style={{ color: "var(--proof-gold)" }}
              variant="outline"
            >
              {polling ? (
                <Loader2 size={14} className="animate-spin mr-2" />
              ) : null}
              Vérifier
            </Button>
          </div>
        </div>
        {isArbiterOrAdmin && (
          <div
            className="pt-3 border-t"
            style={{ borderColor: "var(--graphite-600)" }}
          >
            <div
              className="text-caption-arbitre mb-2"
              style={{ color: "var(--proof-gold)" }}
            >
              Direction de procédure (arbitre)
            </div>
            <AnchorTriggerDialog
              caseId={detail.id}
              disabled={noDocs || alreadyPending}
              onTriggered={onTriggered}
            />
            {noDocs && (
              <p
                className="text-xs mt-2"
                style={{ color: "var(--arbitre-slate-500)" }}
              >
                Au moins une pièce probatoire est requise avant ancrage.
              </p>
            )}
            {alreadyPending && (
              <p
                className="text-xs mt-2"
                style={{ color: "var(--alert-orange)" }}
              >
                Une transaction est déjà en attente de confirmation.
              </p>
            )}
          </div>
        )}
      </div>
      </FormationGate>
    </section>
  );
}

function MembresSection({ members }: { members: CaseDetail["members"] }) {
  return (
    <section id="membres" className="scroll-mt-24">
      <SectionTitle icon={<Users size={12} />}>
        Membres ({members.length})
      </SectionTitle>
      <ul
        className="mt-3 gsf-surface divide-y"
        style={{ borderColor: "var(--graphite-600)" }}
      >
        {members.map((m) => (
          <li
            key={m.user_id}
            className="px-4 py-3 text-sm flex items-center justify-between gap-3"
            style={{
              borderColor: "var(--graphite-600)",
              color: "var(--arbitre-slate-300)",
            }}
          >
            <span className="gsf-mono-proof text-xs truncate">{m.user_id}</span>
            <span
              className="text-caption-arbitre shrink-0"
              style={{ color: "var(--proof-gold)" }}
            >
              {m.member_role}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SkillsSection({
  caseId,
  isArbiterOrAdmin,
}: {
  caseId: number;
  isArbiterOrAdmin: boolean;
}) {
  // Les skills sont des ACTIONS DE PRODUCTION (Artifact + audit chainable).
  // On les expose uniquement aux arbitres/admin — une partie ou un avocat
  // peut consulter les Artifact produits (autre vue) mais ne genere pas
  // de livrable IA cote tribunal.
  if (!isArbiterOrAdmin) {
    return (
      <section id="skills" className="scroll-mt-24">
        <SectionTitle icon={<Sparkles size={12} />}>Skills IA</SectionTitle>
        <p
          className="mt-2 text-xs italic"
          style={{ color: "var(--arbitre-slate-500)", lineHeight: 1.55 }}
        >
          La production d'Artifact IA (chronologie, sections de sentence,
          canevas, controle Art. 20, calcul des frais CCJA) est reservee
          au tribunal. Les parties peuvent consulter les Artifact produits
          dans la vue dediee.
        </p>
      </section>
    );
  }
  return (
    <section id="skills" className="scroll-mt-24">
      <SectionTitle icon={<Sparkles size={12} />}>Skills IA</SectionTitle>
      <p
        className="mt-2 text-xs italic"
        style={{ color: "var(--arbitre-slate-500)", lineHeight: 1.55 }}
      >
        Catalog des skills enregistrees cote core — chaque execution
        materialise un Artifact (DRAFT) lie au dossier, trace dans la
        chaine audit (hash-chained). Le contenu produit est ensuite
        soumis a relecture / approbation / deliberation (Art. 14 AI Act —
        Human-in-the-Loop).
      </p>
      <div className="mt-4 gsf-surface p-5">
        <SkillRunner caseId={caseId} />
      </div>
    </section>
  );
}

function HistoriqueSection({ detail }: { detail: CaseDetail }) {
  return (
    <section id="historique" className="scroll-mt-24">
      <SectionTitle icon={<Clock size={12} />}>Historique</SectionTitle>
      <p
        className="mt-2 text-xs italic"
        style={{ color: "var(--arbitre-slate-500)", lineHeight: 1.55 }}
      >
        Événements traceables dérivés du dossier (ouverture, constitution
        des membres, dépôts de pièces, ancrages). Les transitions workflow
        et signatures de décisions seront ajoutées quand le core exposera
        un endpoint <code className="gsf-mono-proof">/cases/&#123;id&#125;/events</code>.
      </p>
      <div className="mt-4 gsf-surface p-5">
        <CaseTimeline detail={detail} />
      </div>
    </section>
  );
}

function DocumentRow({
  doc,
  onVerify,
  onDownload,
}: {
  doc: CaseDetail["documents"][number];
  onVerify: (ebid: string) => void;
  onDownload: (ebid: string, filename: string) => void;
}) {
  return (
    <article className="gsf-surface p-4 text-sm">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{doc.original_filename}</div>
          <div
            className="text-xs mt-1 flex flex-wrap gap-2 items-center"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            {doc.secure_mime_type && <span>{doc.secure_mime_type}</span>}
            {doc.file_size_bytes && (
              <span>{(doc.file_size_bytes / 1024).toFixed(1)} ko</span>
            )}
            {doc.role_at_upload && (
              <span style={{ color: "var(--proof-gold)" }}>
                {doc.role_at_upload}
              </span>
            )}
            {doc.date_creation && <span>{fmtDate(doc.date_creation)}</span>}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {doc.has_tsa_token ? (
              <ProofBadge
                variant="filled"
                icon={<ShieldCheck size={10} />}
                title="Horodatage qualifié Zetes RFC 3161"
              >
                TSA
              </ProofBadge>
            ) : (
              <ProofBadge variant="missing" title="Pas de jeton TSA">
                TSA absent
              </ProofBadge>
            )}
            {doc.has_merkle_proof ? (
              <ProofBadge
                variant="filled"
                icon={<Network size={10} />}
                title="Inclusion Merkle figée au dépôt"
              >
                Merkle
              </ProofBadge>
            ) : (
              <ProofBadge variant="missing" title="Pas de preuve Merkle">
                Merkle absent
              </ProofBadge>
            )}
          </div>
          <div
            className="mt-2 gsf-mono-proof text-[10px] break-all"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            {doc.file_integrity_hash}
          </div>
        </div>
        <div className="flex flex-row gap-2 shrink-0">
          {doc.evidence_bundle_id && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  onDownload(doc.evidence_bundle_id!, doc.original_filename)
                }
                style={{ color: "var(--proof-gold)" }}
              >
                <Download size={13} className="mr-1" />
                Télécharger
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVerify(doc.evidence_bundle_id!)}
                style={{ color: "var(--proof-gold)" }}
              >
                <ShieldCheck size={13} className="mr-1" />
                Vérifier
              </Button>
            </>
          )}
        </div>
      </header>
    </article>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
}
